import { TFunction } from "i18next";
import _ from "lodash";

import {
  BulkRecalculatePriceSelectionScope,
  IRideTableDto,
  RideStatusEnum,
} from "@ryde/shared";

import {
  BulkRecalculateRateType,
  IBulkRecalculateSelectionCounts,
  IBulkRecalculateSelectionRadioOption,
  IBulkRecalculateSelectionTranslationKeys,
  IBulkRecalculateSwitchCounts,
} from "./bulk-recalculate-selection.models";

export const rideHasClientPriceRate = (ride: IRideTableDto): boolean =>
  Boolean(ride.priceRateId);

export const rideHasSupplierPriceRate = (ride: IRideTableDto): boolean =>
  Boolean(ride.supplierPriceRateId);

export const rideHasRate = (
  ride: IRideTableDto,
  rateType: BulkRecalculateRateType,
): boolean => {
  switch (rateType) {
    case BulkRecalculateRateType.SupplierPriceRate:
      return rideHasSupplierPriceRate(ride);
    default:
      return rideHasClientPriceRate(ride);
  }
};

export const rideHasManualPrice = (
  ride: IRideTableDto,
  rateType: BulkRecalculateRateType,
): boolean => {
  if (rateType === BulkRecalculateRateType.SupplierPriceRate) {
    return !_.isNil(ride.metadata?.supplierManualPrice);
  }

  return !_.isNil(ride.metadata?.manualPrice);
};

export const rideHasDoneStatus = (ride: IRideTableDto): boolean =>
  ride.status === RideStatusEnum.Done ||
  ride.status === RideStatusEnum.DoneApproved;

export const getBulkRecalculateSelectionCounts = (
  selectedRides: IRideTableDto[],
  rateType: BulkRecalculateRateType,
): IBulkRecalculateSelectionCounts => {
  const withPriceRateRideIds: number[] = [];
  const withoutPriceRateRideIds: number[] = [];
  const withClientRateWithoutSupplierRateRideIds: number[] = [];
  const withSupplierRateWithoutClientRateRideIds: number[] = [];

  selectedRides.forEach(ride => {
    const hasClientPriceRate = rideHasClientPriceRate(ride);
    const hasSupplierPriceRate = rideHasSupplierPriceRate(ride);

    if (rideHasRate(ride, rateType)) {
      withPriceRateRideIds.push(ride.id);
    } else {
      withoutPriceRateRideIds.push(ride.id);
    }

    if (hasClientPriceRate && !hasSupplierPriceRate) {
      withClientRateWithoutSupplierRateRideIds.push(ride.id);
    }

    if (hasSupplierPriceRate && !hasClientPriceRate) {
      withSupplierRateWithoutClientRateRideIds.push(ride.id);
    }
  });

  return {
    all: selectedRides.length,
    withPriceRate: withPriceRateRideIds.length,
    withoutPriceRate: withoutPriceRateRideIds.length,
    withPriceRateRideIds,
    withoutPriceRateRideIds,
    withClientRateWithoutSupplierRate:
      withClientRateWithoutSupplierRateRideIds.length,
    withClientRateWithoutSupplierRateRideIds,
    withSupplierRateWithoutClientRate:
      withSupplierRateWithoutClientRateRideIds.length,
    withSupplierRateWithoutClientRateRideIds,
  };
};

export const getBulkRecalculateSelectedRideIds = (
  selectionScope: BulkRecalculatePriceSelectionScope,
  selectionCounts: IBulkRecalculateSelectionCounts,
  selectedRideIds: number[],
): number[] => {
  switch (selectionScope) {
    case BulkRecalculatePriceSelectionScope.WithPriceRateOnly:
      return selectionCounts.withPriceRateRideIds;
    case BulkRecalculatePriceSelectionScope.WithoutPriceRateOnly:
      return selectionCounts.withoutPriceRateRideIds;
    case BulkRecalculatePriceSelectionScope.WithClientPriceRateWithoutSupplierPriceRate:
      return selectionCounts.withClientRateWithoutSupplierRateRideIds;
    case BulkRecalculatePriceSelectionScope.WithSupplierPriceRateWithoutClientPriceRate:
      return selectionCounts.withSupplierRateWithoutClientRateRideIds;
    default:
      return selectedRideIds;
  }
};

export const getBulkRecalculateSwitchCounts = (
  rides: IRideTableDto[],
  rateType: BulkRecalculateRateType,
): IBulkRecalculateSwitchCounts => {
  let withManualPrice = 0;
  let withDoneStatus = 0;

  rides.forEach(ride => {
    if (rideHasManualPrice(ride, rateType)) {
      withManualPrice += 1;
    }

    if (rideHasDoneStatus(ride)) {
      withDoneStatus += 1;
    }
  });

  return {
    withManualPrice,
    withDoneStatus,
  };
};

export const getBulkRecalculateSelectionTranslationKeys = (
  rateType: BulkRecalculateRateType,
): IBulkRecalculateSelectionTranslationKeys => {
  const isSupplierPriceRate =
    rateType === BulkRecalculateRateType.SupplierPriceRate;

  return {
    withRateLabelKey: isSupplierPriceRate
      ? "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_WITH_SUPPLIER_PRICE_RATE"
      : "general.RECALCULATE_PRICE_SELECTION_WITH_PRICE_RATE",
    withoutRateLabelKey: isSupplierPriceRate
      ? "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_WITHOUT_SUPPLIER_PRICE_RATE"
      : "general.RECALCULATE_PRICE_SELECTION_WITHOUT_PRICE_RATE",
    allHelperKey: isSupplierPriceRate
      ? "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_ALL_HELPER"
      : "general.RECALCULATE_PRICE_SELECTION_ALL_HELPER",
    withRateHelperKey: isSupplierPriceRate
      ? "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_WITH_SUPPLIER_PRICE_RATE_HELPER"
      : "general.RECALCULATE_PRICE_SELECTION_WITH_PRICE_RATE_HELPER",
    withoutRateHelperKey: isSupplierPriceRate
      ? "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_WITHOUT_SUPPLIER_PRICE_RATE_HELPER"
      : "general.RECALCULATE_PRICE_SELECTION_WITHOUT_PRICE_RATE_HELPER",
  };
};

export const getBulkRecalculateSelectionRadioOptions = (
  rateType: BulkRecalculateRateType,
  selectionCounts: IBulkRecalculateSelectionCounts,
  t: TFunction,
): IBulkRecalculateSelectionRadioOption[] => {
  const translationKeys = getBulkRecalculateSelectionTranslationKeys(rateType);

  const options: IBulkRecalculateSelectionRadioOption[] = [
    {
      value: BulkRecalculatePriceSelectionScope.All,
      label: t("general.RECALCULATE_PRICE_SELECTION_ALL", {
        count: selectionCounts.all,
      }),
      helperText: t(translationKeys.allHelperKey),
      disabled: selectionCounts.all === 0,
    },
    {
      value: BulkRecalculatePriceSelectionScope.WithPriceRateOnly,
      label: t(translationKeys.withRateLabelKey, {
        count: selectionCounts.withPriceRate,
      }),
      helperText: t(translationKeys.withRateHelperKey),
      disabled: selectionCounts.withPriceRate === 0,
    },
    {
      value: BulkRecalculatePriceSelectionScope.WithoutPriceRateOnly,
      label: t(translationKeys.withoutRateLabelKey, {
        count: selectionCounts.withoutPriceRate,
      }),
      helperText: t(translationKeys.withoutRateHelperKey),
      disabled: selectionCounts.withoutPriceRate === 0,
    },
  ];

  if (rateType !== BulkRecalculateRateType.SupplierPriceRate) {
    return options;
  }

  return [
    ...options,
    {
      value:
        BulkRecalculatePriceSelectionScope.WithClientPriceRateWithoutSupplierPriceRate,
      label: t(
        "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_CLIENT_RATE_WITHOUT_SUPPLIER_RATE",
        {
          count: selectionCounts.withClientRateWithoutSupplierRate,
        },
      ),
      helperText: t(
        "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_CLIENT_RATE_WITHOUT_SUPPLIER_RATE_HELPER",
      ),
      disabled: selectionCounts.withClientRateWithoutSupplierRate === 0,
    },
    {
      value:
        BulkRecalculatePriceSelectionScope.WithSupplierPriceRateWithoutClientPriceRate,
      label: t(
        "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_SUPPLIER_RATE_WITHOUT_CLIENT_RATE",
        {
          count: selectionCounts.withSupplierRateWithoutClientRate,
        },
      ),
      helperText: t(
        "general.RECALCULATE_SUPPLIER_PRICE_SELECTION_SUPPLIER_RATE_WITHOUT_CLIENT_RATE_HELPER",
      ),
      disabled: selectionCounts.withSupplierRateWithoutClientRate === 0,
    },
  ];
};
